/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation } from '@tanstack/react-query';

import { NodeConfigResponse, ApiError } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type UploadNodeConfigMutationRequest = {
  formData: {
    file: Blob | File;
    configSchema: string;
    name: string;
    os: 'windows' | 'linux';
    description: string;
    subtenantId?: string;
  };
};

export type UploadNodeConfigMutationConfig = {
  onSuccess?: (data: NodeConfigResponse, variables: UploadNodeConfigMutationRequest) => void;
  onError?: (error: unknown, variables: UploadNodeConfigMutationRequest) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
  onMutate?: (variables: UploadNodeConfigMutationRequest) => Promise<unknown> | void;
};

/**
 * Hook for uploading a new node config through the management plane API.
 *
 * @param config.onSuccess - Called after successful node config upload
 * @param config.onError - Called if upload fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for uploading node config
 *
 * @example
 * ```tsx
 * const { mutate } = useUploadNodeConfigMutation({
 *   onSuccess: (data) => console.log('Node config uploaded:', data),
 *   onError: (error) => console.error('Node config upload failed:', error)
 * });
 *
 * // Upload Node Config
 * mutate({
 *   tenantId: 'tenant-uuid',
 *   requestBody: {
 *     configSchema: 'config content',
 *     name: 'My Config',
 *     os: 'linux',
 *     description: 'Config description',
 *     subtenantId: 'subtenant-uuid' // optional
 *   }
 * });
 * ```
 */

export function useUploadNodeConfigMutation({
  onSuccess,
  onError,
  onSettled,
  onMutate,
}: UploadNodeConfigMutationConfig) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutate, isPending, isError, error } = useMutation<
    NodeConfigResponse,
    ApiError,
    UploadNodeConfigMutationRequest
  >({
    mutationFn: ({ formData }) => {
      return mgmtPlaneAPIClient.nodeConfigs.uploadNodeConfig({
        tenantId: tenantId!,
        formData,
      });
    },
    onSuccess,
    onError,
    onSettled,
    onMutate,
  });

  return { mutate, isPending, isError, error };
}
